/**
 * Mid/Small Sleeve — the backtested ungated top-30 mid/small gems config
 * with binary-event veto. Risk is managed by sizing, not filtering.
 */
import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { API } from "./statsApi";

const fmtPct = (v, dp = 0) =>
  v == null ? "-" : `${v > 0 ? "+" : ""}${(v * 100).toFixed(dp)}%`;

const StateChip = ({ state }) =>
  state ? (
    <Badge className={`text-[10px] border ${
      state === "Quiet" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
        : state === "Hot" ? "bg-red-500/15 text-red-400 border-red-500/30"
        : "bg-muted text-muted-foreground border-border"}`}>
      {state}
    </Badge>
  ) : <span className="text-xs text-muted-foreground">—</span>;

const EVIDENCE = [
  { label: "Cumulative", value: "+233.8%", sub: "Feb 2023 → Jul 2026" },
  { label: "Sharpe", value: "1.39", sub: "EW top-30 · monthly" },
  { label: "Excess vs SPY", value: "+2.37%/mo", sub: "t = 1.47" },
  { label: "Config", value: "Ungated", sub: "Quiet gate rejected (t 1.19 vs 1.67)" },
];

export const MidSmallSleeve = ({ token, dates }) => {
  const [selDate, setSelDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (dates?.length && !selDate) setSelDate(dates[0]);
  }, [dates, selDate]);

  const load = useCallback(async (refresh = false) => {
    if (!selDate) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/stats/hidden-gems/screener?date=${selDate}&gate=all&cap=mid_small&top_n=30${refresh ? "&refresh=true" : ""}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json()).detail || res.status);
      setData(await res.json());
    } catch (e) {
      toast.error(`Mid/small sleeve failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [selDate, token]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const held = rows.filter((r) => !r.binary_risk);
  const vetoed = rows.filter((r) => r.binary_risk);

  return (
    <div className="space-y-5" data-testid="midsmall-sleeve">
      <div className="flex items-center gap-3 flex-wrap">
        <select
          data-testid="midsmall-date-select"
          className="h-9 w-40 rounded-md border border-border bg-background px-2 text-sm"
          value={selDate}
          onChange={(e) => setSelDate(e.target.value)}
        >
          {(dates || []).slice(0, 200).map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <Button variant="outline" size="sm" disabled={loading} onClick={() => load(true)}
                data-testid="midsmall-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Recompute
        </Button>
        {data && (
          <span className="text-xs text-muted-foreground font-mono">
            as of {data.resolved_date}
            {data.options_as_of !== data.resolved_date ? ` · options ${data.options_as_of}` : ""} ·{" "}
            {held.length} held · {vetoed.length} vetoed {data.cached ? "· cached" : ""}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="midsmall-evidence">
        {EVIDENCE.map((e) => (
          <Card key={e.label} className="border-amber-500/20 bg-gradient-to-br from-amber-500/[0.05] to-transparent">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{e.label}</div>
              <div className="text-xl font-bold font-mono text-amber-300">{e.value}</div>
              <div className="text-[10px] text-muted-foreground">{e.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-border/60">
        <CardContent className="p-3 text-[11px] text-muted-foreground space-y-1">
          <p>
            <span className="text-foreground font-semibold">How to trade it:</span>{" "}
            equal-weight the held names, rebalance monthly. Return profile is tail-driven
            (~40-50% hit rate — a few +100% winners carry the sleeve), so the full 30-name
            breadth is required. Top-10 concentration was rejected (+204% but 54% vol, −39.5% DD).
          </p>
          <p>
            <span className="text-foreground font-semibold">Risk by sizing, not filtering:</span>{" "}
            the −29.5% book drawdown is small-cap beta — size the sleeve as a satellite
            (it complements the big-cap converged CORE), don't add more filters. The only veto kept is
            binary-event risk (ATM IV ≥ 100% or expected move ≥ 30%): historically ~6% of names,
            free insurance — returns equal or better with traps reduced 4.2% → 3.6%.
          </p>
        </CardContent>
      </Card>

      {loading && !data ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Computing…</div>
      ) : (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Held — {held.length} names (gem score, ungated)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="midsmall-table">
                  <thead className="border-b border-border text-muted-foreground">
                    <tr>
                      <th className="py-1.5 px-2 text-left">#</th>
                      <th className="py-1.5 px-2 text-left">Symbol</th>
                      <th className="py-1.5 px-2 text-right">Gem score</th>
                      <th className="py-1.5 px-2 text-right">Price</th>
                      <th className="py-1.5 px-2 text-right">Kensys target</th>
                      <th className="py-1.5 px-2 text-right">Analyst T</th>
                      <th className="py-1.5 px-2 text-left">Tape</th>
                      <th className="py-1.5 px-2 text-right" title="Microstate alpha, next 3M">µα 3M</th>
                      <th className="py-1.5 px-2 text-left">Regime</th>
                      <th className="py-1.5 px-2 text-right">Funds</th>
                      <th className="py-1.5 px-2 text-left">Top specialists</th>
                    </tr>
                  </thead>
                  <tbody>
                    {held.map((r, i) => (
                      <tr key={r.symbol} data-testid={`midsmall-row-${r.symbol}`}
                          className="border-b border-border/40 hover:bg-muted/40">
                        <td className="py-1.5 px-2 text-muted-foreground">{i + 1}</td>
                        <td className="py-1.5 px-2 font-semibold">{r.symbol}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-amber-300 font-semibold">
                          {r.gem_score}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono">{r.price ?? "-"}</td>
                        <td className="py-1.5 px-2 text-right font-mono">
                          {r.kensys_target ?? "-"}
                          {r.kensys_upside != null && (
                            <span className={`ml-1 ${r.kensys_upside >= 0 ? "text-green-400" : "text-red-400"}`}>
                              {fmtPct(r.kensys_upside)}
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">
                          {r.analyst_target ?? "-"}
                          {r.analyst_upside != null && <span className="ml-1">{fmtPct(r.analyst_upside)}</span>}
                        </td>
                        <td className="py-1.5 px-2"><StateChip state={r.options_state} /></td>
                        <td className={`py-1.5 px-2 text-right font-mono ${
                          r.micro_alpha_3m > 0 ? "text-green-400" : r.micro_alpha_3m < 0 ? "text-red-400" : "text-muted-foreground"}`}>
                          {r.micro_alpha_3m != null ? `${r.micro_alpha_3m > 0 ? "+" : ""}${r.micro_alpha_3m}%` : "-"}
                        </td>
                        <td className="py-1.5 px-2 whitespace-nowrap text-muted-foreground">{r.regime || "—"}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{r.n_funds ?? "-"}</td>
                        <td className="py-1.5 px-2 text-muted-foreground max-w-[180px] truncate">
                          {r.specialists?.map((s) => s.name).slice(0, 2).join(", ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {vetoed.length > 0 && (
            <Card className="border-amber-500/30" data-testid="midsmall-vetoed">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-amber-400">
                  Vetoed — binary-event risk ({vetoed.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="flex gap-2 flex-wrap">
                {vetoed.map((r) => (
                  <Badge key={r.symbol}
                         title={`ATM IV ${r.atm_iv != null ? (r.atm_iv * 100).toFixed(0) + "%" : "n/a"} · expected move ${r.expected_move != null ? (r.expected_move * 100).toFixed(0) + "%" : "n/a"}`}
                         className="bg-amber-500/15 text-amber-300 border border-amber-500/40 cursor-help font-mono">
                    {r.symbol}
                  </Badge>
                ))}
              </CardContent>
            </Card>
          )}

          <p className="text-[11px] text-muted-foreground font-mono">
            Ranked by gem_score (specialist accumulation composite) within the mid/small bucket
            (&lt;$1B floor of the top-800 mid/smalls) · no options gate · binary veto: ATM IV ≥ 100%
            or expected move ≥ 30% · Backtest Feb 2023 → Jul 2026.
          </p>
        </>
      )}
    </div>
  );
};
